import { FC, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useParams } from "react-router-dom";

import { BAND_PATHS } from "Bands/router";
import { dataMetaSelector, getUserPermissions, userPermissionsSelectorCreator } from "Bands/state";
import { BAND_ACTIONS } from "Bands/types";
import { useBandRoute, useClearActionMetaOnUnMount } from "Bands/utils";
import { copy } from "shared/assets";
import { BackButton, Card, Loader } from "shared/components";
import { Spacer } from "shared/layout";
import { FourOhFour } from "shared/pages";
import { MainContent } from "shared/styles";
import { IAPIState } from "shared/types";

import { useBandContext } from "Bands/contexts";
import { EditPermissions } from "./containers";

const { permissions } = copy;

export const EditPermissionsPage: FC = () => {
  const dispatch = useDispatch();
  const { userId: editUserId } = useParams<{ userId: string }>();
  const { getPath } = useBandRoute();
  const { state }: { state: Record<string, string> } = useLocation();
  const { allBandUsers } = useBandContext();
  const [forceUserId, setForceUserId] = useState<string>();

  const usersWithPermissionsSelector = useMemo(
    () => userPermissionsSelectorCreator(forceUserId || editUserId),
    [editUserId, forceUserId]
  );
  const usersWithPermissions = useSelector(usersWithPermissionsSelector);

  const { api: apiMeta } = useSelector(dataMetaSelector(BAND_ACTIONS.getUserPermissions));

  useClearActionMetaOnUnMount(BAND_ACTIONS.getUserPermissions);

  useEffect(() => {
    dispatch(getUserPermissions(editUserId));
  }, [dispatch, editUserId]);

  const renderContent = () => {
    if (apiMeta?.status === IAPIState.pending) {
      return <Loader />;
    }

    if (!usersWithPermissions)
      return (
        <Card title={`${copy.permissions.editPermissions.title}...`}>
          <Loader />
        </Card>
      );

    return (
      <EditPermissions
        user={allBandUsers.find(user => user.id === editUserId)!}
        userPermissions={usersWithPermissions.permissions}
        setForceUserId={setForceUserId}
      />
    );
  };

  if (!usersWithPermissions && apiMeta?.status !== IAPIState.pending) return <FourOhFour />;

  return (
    <Spacer size="s">
      {state?.from ? (
        <BackButton to={state.from} label={permissions.back} />
      ) : (
        <BackButton to={getPath(BAND_PATHS.MEMBERS)} label={permissions.backMembers} />
      )}
      <MainContent>{renderContent()}</MainContent>
    </Spacer>
  );
};
